import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useKeepInViewport } from '../hooks/useKeepInViewport';
import { useBoundaries } from '../hooks/useBoundaries';
import BoundaryDivider from './BoundaryDivider';

// Value format: "YYYY".
interface YearPickerProps {
  value: string;
  onChange: (value: string) => void;
  max?: string;        // "YYYY" inclusive upper bound (optional)
  className?: string;
}

export default function YearPicker({ value, onChange, max, className }: YearPickerProps) {
  const [open, setOpen] = useState(false);
  const selYear = parseInt(value, 10);
  const maxYear = max ? parseInt(max, 10) : new Date().getFullYear();
  // The 12-year window shown in the grid ends at `viewEnd`.
  const [viewEnd, setViewEnd] = useState<number>(Math.min(selYear, maxYear));
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useKeepInViewport(open); // stays inside a narrow screen
  const boundaries = useBoundaries();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Escape closes it and puts focus back on the button, as for any popup.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (!open) setViewEnd(Math.min(selYear, maxYear));
    setOpen((o) => !o);
  };

  const pick = (y: number) => {
    if (max && y > maxYear) return;
    onChange(String(y));
    setOpen(false);
  };

  // 12 years ending at viewEnd (newest last), oldest first.
  const years = Array.from({ length: 12 }, (_, i) => viewEnd - 11 + i);

  // Years that ended before the Jira conventions started are a section of their own (the year they began in is not).
  const isEarlier = (y: number) => !!boundaries && y < Number(boundaries.month.slice(0, 4));
  const sections = [
    { earlier: true, list: years.filter(isEarlier) },
    { earlier: false, list: years.filter((y) => !isEarlier(y)) },
  ].filter((section) => section.list.length > 0);

  return (
    <div ref={ref} className={`relative ${className || ''}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100"
      >
        <Calendar className="h-4 w-4 text-gray-400" />
        {value || 'Select year'}
      </button>

      {open && (
        <div ref={popRef} role="group" aria-label="Choose a year" className="absolute right-0 z-50 mt-2 w-64 max-w-[calc(100vw-1rem)] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl ring-1 ring-black/5">
          {/* Decade navigator */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewEnd((y) => y - 12)}
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
              aria-label="Earlier years"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900">{years[0]} – {years[years.length - 1]}</span>
            <button
              type="button"
              disabled={viewEnd >= maxYear}
              onClick={() => setViewEnd((y) => Math.min(y + 12, maxYear))}
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Later years"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Year grid: the years before the Jira conventions started sit apart, under their own caption. */}
          {sections.map(({ earlier, list }) => (
            <div key={earlier ? 'before' : 'from'}>
              {boundaries && (earlier || sections.length > 1) && <BoundaryDivider label={earlier ? boundaries.label : boundaries.fromLabel} />}
              <div className="grid grid-cols-3 gap-1.5">
                {list.map((y) => {
                  const selected = y === selYear;
                  const disabled = !!max && y > maxYear;
                  return (
                    <button
                      key={y}
                      type="button"
                      disabled={disabled}
                      onClick={() => pick(y)}
                      className={`rounded-lg px-2 py-2 text-sm font-medium transition-colors ${
                        selected
                          ? 'bg-blue-600 text-white shadow-sm'
                          : disabled
                          ? 'cursor-not-allowed text-gray-300'
                          : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
